import { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { getMachineAvailability, AVAILABILITY, ENGINE_TYPES, STATUSES } from '../constants';
import type { Machine, Site, AvailabilityKey, EngineTypeKey, StatusKey, PickupKey, DashboardFilterKey } from '../types';
import { normalizeForCompare } from '../../../utils/normalizeText';
import { appNow } from '../../../utils/timezone';

export interface OptionGroup {
  key: string;
  label: string;
}

const STORAGE_KEY = 'planly_filters';
const DASHBOARD_FILTER_KEY = 'planly_dashboard_filter';
const DASHBOARD_FILTER_MACHINE_IDS_KEY = 'planly_dashboard_filter_machine_ids';

interface ConsumedDashboardFilter {
  key: DashboardFilterKey;
  machineIds: string[] | null;
}

function consumeDashboardFilter(): ConsumedDashboardFilter | null {
  try {
    const val = localStorage.getItem(DASHBOARD_FILTER_KEY);
    if (val === 'arriving' || val === 'pickup_confirmed' || val === 'pickup_action' || val === 'to_order_action') {
      localStorage.removeItem(DASHBOARD_FILTER_KEY);
      return { key: val, machineIds: null };
    }
    if (val === 'rental_extension' || val === 'overdue_return') {
      localStorage.removeItem(DASHBOARD_FILTER_KEY);
      const idsRaw = localStorage.getItem(DASHBOARD_FILTER_MACHINE_IDS_KEY);
      localStorage.removeItem(DASHBOARD_FILTER_MACHINE_IDS_KEY);
      const ids: unknown = idsRaw ? JSON.parse(idsRaw) : null;
      return {
        key: val,
        machineIds: Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : null,
      };
    }
  } catch { /* ignore */ }
  return null;
}

const ALL_AVAILABILITY = Object.keys(AVAILABILITY) as AvailabilityKey[];
const ALL_STATUS = Object.keys(STATUSES) as StatusKey[];
const ALL_ENGINE_TYPE = Object.keys(ENGINE_TYPES) as EngineTypeKey[];
const ALL_PICKUP: PickupKey[] = ['confirmed', 'not_confirmed'];
const DEFAULT_STATUS: StatusKey[] = ['to_order', 'ordered'];

interface StoredFilters {
  availability: AvailabilityKey[];
  status: StatusKey[];
  engineType: EngineTypeKey[];
  sites: string[];
  suppliers: string[];
  subcontractors: string[];
  categories: string[];
  options: string[];
  pickup: PickupKey[];
  search: string;
}

function loadFilters(pickupHandinEnabled: boolean): StoredFilters {
  const allAvailability = pickupHandinEnabled ? ALL_AVAILABILITY : ALL_AVAILABILITY.filter((k) => k !== 'reserved');
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { availability: allAvailability, status: DEFAULT_STATUS, engineType: ALL_ENGINE_TYPE, sites: [], suppliers: [], subcontractors: [], categories: [], options: [], pickup: ALL_PICKUP, search: '' };
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    const availSet = new Set(allAvailability);
    const statSet = new Set(ALL_STATUS);
    const engineTypeSet = new Set(ALL_ENGINE_TYPE);
    const pickupSet = new Set(ALL_PICKUP);
    return {
      availability: Array.isArray(parsed.availability)
        ? parsed.availability.filter((k): k is AvailabilityKey => availSet.has(k as AvailabilityKey))
        : allAvailability,
      status: Array.isArray(parsed.status)
        ? parsed.status.filter((k): k is StatusKey => statSet.has(k as StatusKey))
        : DEFAULT_STATUS,
      engineType: Array.isArray(parsed.engineType)
        ? parsed.engineType.filter((k): k is EngineTypeKey => engineTypeSet.has(k as EngineTypeKey))
        : ALL_ENGINE_TYPE,
      sites: Array.isArray(parsed.sites)
        ? parsed.sites.filter((s): s is string => typeof s === 'string')
        : [],
      suppliers: Array.isArray(parsed.suppliers)
        ? parsed.suppliers.filter((s): s is string => typeof s === 'string')
        : [],
      subcontractors: Array.isArray(parsed.subcontractors)
        ? parsed.subcontractors.filter((s): s is string => typeof s === 'string')
        : [],
      categories: Array.isArray(parsed.categories)
        ? parsed.categories.filter((s): s is string => typeof s === 'string')
        : [],
      options: Array.isArray(parsed.options)
        ? parsed.options.filter((s): s is string => typeof s === 'string')
        : [],
      pickup: Array.isArray(parsed.pickup)
        ? parsed.pickup.filter((k): k is PickupKey => pickupSet.has(k as PickupKey))
        : ALL_PICKUP,
      search: typeof parsed.search === 'string' ? parsed.search : '',
    };
  } catch {
    return { availability: allAvailability, status: DEFAULT_STATUS, engineType: ALL_ENGINE_TYPE, sites: [], suppliers: [], subcontractors: [], categories: [], options: [], pickup: ALL_PICKUP, search: '' };
  }
}

function saveFilters(f: StoredFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    // localStorage unavailable (e.g., private browsing with strict settings)
  }
}

interface UsePlanningFiltersReturn {
  availabilityFilter: Set<AvailabilityKey>;
  toggleAvailability: (key: AvailabilityKey) => void;
  setAvailabilityFilter: (f: Set<AvailabilityKey>) => void;
  statusFilter: Set<StatusKey>;
  toggleStatus: (key: StatusKey) => void;
  setStatusFilter: (f: Set<StatusKey>) => void;
  engineTypeFilter: Set<EngineTypeKey>;
  toggleEngineType: (key: EngineTypeKey) => void;
  setEngineTypeFilter: (f: Set<EngineTypeKey>) => void;
  siteFilter: Set<string>;
  toggleSite: (id: string) => void;
  setSiteFilter: (f: Set<string>) => void;
  supplierFilter: Set<string>;
  toggleSupplier: (id: string) => void;
  setSupplierFilter: (f: Set<string>) => void;
  uniqueSuppliers: { id: string; name: string }[];
  subcontractorFilter: Set<string>;
  toggleSubcontractor: (id: string) => void;
  setSubcontractorFilter: (f: Set<string>) => void;
  sortedSubcontractors: { id: string; name: string }[];
  categoryFilter: Set<string>;
  toggleCategory: (category: string) => void;
  setCategoryFilter: (f: Set<string>) => void;
  sortedCategories: string[];
  optionsFilter: Set<string>;
  toggleOption: (key: string) => void;
  setOptionsFilter: (f: Set<string>) => void;
  sortedOptions: OptionGroup[];
  pickupFilter: Set<PickupKey>;
  togglePickup: (key: PickupKey) => void;
  setPickupFilter: (f: Set<PickupKey>) => void;
  dashboardFilter: DashboardFilterKey | null;
  clearDashboardFilter: () => void;
  sortedSites: Site[];
  search: string;
  setSearch: (v: string) => void;
  filterOpen: boolean;
  setFilterOpen: (v: boolean) => void;
  filteredMachines: Machine[];
  activeFilterCount: number;
}

export function usePlanningFilters(machines: Machine[], sites: Site[], pickupHandinEnabled: boolean): UsePlanningFiltersReturn {
  // 'reserved' is only a valid availability key while the org's pickup/hand-in check is
  // enabled — otherwise it never occurs, so it's excluded from "all" everywhere below.
  const availabilityKeys = pickupHandinEnabled ? ALL_AVAILABILITY : ALL_AVAILABILITY.filter((k) => k !== 'reserved');
  const stored = useMemo(() => loadFilters(pickupHandinEnabled), [pickupHandinEnabled]);

  const [availabilityFilter, setAvailabilityFilter] = useState<Set<AvailabilityKey>>(
    () => new Set(stored.availability.length > 0 ? stored.availability : availabilityKeys)
  );
  // useOrgFeatures defaults pickupHandinEnabled to false while its Supabase lookup is in
  // flight, so this hook's very first mount can compute the "everything selected" default
  // above without 'reserved' — and since useState's lazy initializer only ever runs once,
  // that default never picks up 'reserved' later even after pickupHandinEnabled turns true.
  // Re-sync when it does, but only while the filter is still exactly the stale all-but-
  // reserved set (never overriding a real user selection, e.g. one that deliberately
  // unchecked only "Gereserveerd").
  useEffect(() => {
    if (!pickupHandinEnabled) return;
    setAvailabilityFilter((prev) => {
      const nonReserved = ALL_AVAILABILITY.filter((k) => k !== 'reserved');
      const isStaleDefault = prev.size === nonReserved.length && nonReserved.every((k) => prev.has(k));
      return isStaleDefault ? new Set(ALL_AVAILABILITY) : prev;
    });
  }, [pickupHandinEnabled]);
  const [statusFilter, setStatusFilter] = useState<Set<StatusKey>>(
    () => new Set(stored.status.length > 0 ? stored.status : DEFAULT_STATUS)
  );
  const [engineTypeFilter, setEngineTypeFilter] = useState<Set<EngineTypeKey>>(
    () => new Set(stored.engineType.length > 0 ? stored.engineType : ALL_ENGINE_TYPE)
  );
  // Empty set = "all sites" (no filter). Populated = filter to these site IDs.
  const [siteFilter, setSiteFilter] = useState<Set<string>>(() => new Set(stored.sites));
  // Empty set = "all suppliers" (no filter). Populated = filter to these supplier IDs.
  const [supplierFilter, setSupplierFilter] = useState<Set<string>>(() => new Set(stored.suppliers));
  // Empty set = "all subcontractors" (no filter). Populated = filter to these subcontractor IDs.
  const [subcontractorFilter, setSubcontractorFilter] = useState<Set<string>>(() => new Set(stored.subcontractors));
  // Empty set = "all categories" (no filter). Populated = filter to these category values.
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(() => new Set(stored.categories));
  // Empty set = "all options" (no filter). Populated = filter to these normalized option keys.
  const [optionsFilter, setOptionsFilter] = useState<Set<string>>(() => new Set(stored.options));
  const [pickupFilter, setPickupFilter] = useState<Set<PickupKey>>(
    () => new Set(stored.pickup.length > 0 ? stored.pickup : ALL_PICKUP)
  );
  const [search, setSearchRaw] = useState(stored.search);
  const [filterOpen, setFilterOpen] = useState(false);
  const [initialDashboardFilter] = useState(consumeDashboardFilter);
  const [dashboardFilter, setDashboardFilter] = useState<DashboardFilterKey | null>(
    initialDashboardFilter?.key ?? null
  );
  const [dashboardFilterMachineIds, setDashboardFilterMachineIds] = useState<string[] | null>(
    initialDashboardFilter?.machineIds ?? null
  );

  function clearDashboardFilter() {
    setDashboardFilter(null);
    setDashboardFilterMachineIds(null);
  }

  // Persist filter state to localStorage on every change
  useEffect(() => {
    saveFilters({
      availability: [...availabilityFilter],
      status: [...statusFilter],
      engineType: [...engineTypeFilter],
      sites: [...siteFilter],
      suppliers: [...supplierFilter],
      subcontractors: [...subcontractorFilter],
      categories: [...categoryFilter],
      options: [...optionsFilter],
      pickup: [...pickupFilter],
      search,
    });
  }, [availabilityFilter, statusFilter, engineTypeFilter, siteFilter, supplierFilter, subcontractorFilter, categoryFilter, optionsFilter, pickupFilter, search]);

  // Reset site filter when project changes (stale site IDs from previous project)
  const siteIdsKey = sites.map((s) => s.id).sort().join(',');
  useEffect(() => {
    if (sites.length === 0) return;
    setSiteFilter((prev) => {
      if (prev.size === 0) return prev; // Empty = "all", nothing to validate
      const validIds = new Set(sites.map((s) => s.id));
      const hasAnyValid = [...prev].some((id) => validIds.has(id));
      return hasAnyValid ? prev : new Set(); // Reset to "all" if all stored IDs are stale
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteIdsKey]);

  function toggleAvailability(key: AvailabilityKey) {
    setAvailabilityFilter((prev) => {
      const isAll = availabilityKeys.every((k) => prev.has(k));
      if (isAll) {
        // From "all" state → select only this one key
        return new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (next.size === 0) return new Set(availabilityKeys); // Last deselected → reset to all
      } else {
        next.add(key);
        if (availabilityKeys.every((k) => next.has(k))) return new Set(availabilityKeys); // All selected → reset to all
      }
      return next;
    });
  }

  function toggleStatus(key: StatusKey) {
    setStatusFilter((prev) => {
      const isAll = ALL_STATUS.every((k) => prev.has(k));
      if (isAll) {
        // From "all" state → select only this one key
        return new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (next.size === 0) return new Set(ALL_STATUS); // Last deselected → reset to all
      } else {
        next.add(key);
        if (ALL_STATUS.every((k) => next.has(k))) return new Set(ALL_STATUS); // All selected → reset to all
      }
      return next;
    });
  }

  function toggleEngineType(key: EngineTypeKey) {
    setEngineTypeFilter((prev) => {
      const isAll = ALL_ENGINE_TYPE.every((k) => prev.has(k));
      if (isAll) {
        // From "all" state → select only this one key
        return new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (next.size === 0) return new Set(ALL_ENGINE_TYPE); // Last deselected → reset to all
      } else {
        next.add(key);
        if (ALL_ENGINE_TYPE.every((k) => next.has(k))) return new Set(ALL_ENGINE_TYPE); // All selected → reset to all
      }
      return next;
    });
  }

  function toggleSite(id: string) {
    setSiteFilter((prev) => {
      const allSiteIds = sites.map((s) => s.id);
      if (prev.size === 0) {
        // Currently "all sites" → selecting one means "filter by this site only"
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        const validRemaining = allSiteIds.filter((sid) => next.has(sid));
        if (validRemaining.length === 0) return new Set(); // Last deselected → reset to all
      } else {
        next.add(id);
        if (allSiteIds.every((sid) => next.has(sid))) return new Set(); // All selected → reset to all
      }
      return next;
    });
  }

  function togglePickup(key: PickupKey) {
    setPickupFilter((prev) => {
      const isAll = ALL_PICKUP.every((k) => prev.has(k));
      if (isAll) return new Set([key]);
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (next.size === 0) return new Set(ALL_PICKUP);
      } else {
        next.add(key);
        if (ALL_PICKUP.every((k) => next.has(k))) return new Set(ALL_PICKUP);
      }
      return next;
    });
  }

  function setSearch(v: string) {
    setSearchRaw(v);
  }

  const uniqueSuppliers = useMemo(() => {
    const seen = new Map<string, { id: string; name: string }>();
    machines.forEach((m) => {
      if (m.supplier_id && m.supplier && !seen.has(m.supplier_id)) {
        seen.set(m.supplier_id, { id: m.supplier_id, name: m.supplier.name });
      }
    });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [machines]);

  // Reset supplier filter when project changes (stale supplier IDs from previous project)
  const supplierIdsKey = uniqueSuppliers.map((s) => s.id).sort().join(',');
  useEffect(() => {
    if (uniqueSuppliers.length === 0) return;
    setSupplierFilter((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(uniqueSuppliers.map((s) => s.id));
      const hasAnyValid = [...prev].some((id) => validIds.has(id));
      return hasAnyValid ? prev : new Set();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierIdsKey]);

  function toggleSupplier(id: string) {
    setSupplierFilter((prev) => {
      const allSupplierIds = uniqueSuppliers.map((s) => s.id);
      if (prev.size === 0) {
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        const validRemaining = allSupplierIds.filter((sid) => next.has(sid));
        if (validRemaining.length === 0) return new Set();
      } else {
        next.add(id);
        if (allSupplierIds.every((sid) => next.has(sid))) return new Set();
      }
      return next;
    });
  }

  // Only include subcontractors that appear in at least one currently active machine booking,
  // sorted by booking count — same rule as sortedSites.
  const sortedSubcontractors = useMemo(() => {
    const today = appNow().format('YYYY-MM-DD');
    const counts: Record<string, number> = {};
    const names: Record<string, string> = {};
    machines.forEach((m) => {
      m.bookings?.forEach((bk) => {
        if (!bk.subcontractor_id) return;
        if (bk.start_date > today || bk.end_date < today) return;
        counts[bk.subcontractor_id] = (counts[bk.subcontractor_id] ?? 0) + 1;
        if (bk.subcontractor?.name) names[bk.subcontractor_id] = bk.subcontractor.name;
      });
    });
    return Object.keys(counts)
      .map((id) => ({ id, name: names[id] ?? '' }))
      .sort((a, b) => counts[b.id] - counts[a.id]);
  }, [machines]);

  // Reset subcontractor filter when project changes (stale IDs from previous project)
  const subcontractorIdsKey = sortedSubcontractors.map((s) => s.id).sort().join(',');
  useEffect(() => {
    if (sortedSubcontractors.length === 0) return;
    setSubcontractorFilter((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(sortedSubcontractors.map((s) => s.id));
      const hasAnyValid = [...prev].some((id) => validIds.has(id));
      return hasAnyValid ? prev : new Set();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorIdsKey]);

  function toggleSubcontractor(id: string) {
    setSubcontractorFilter((prev) => {
      const allSubcontractorIds = sortedSubcontractors.map((s) => s.id);
      if (prev.size === 0) {
        return new Set([id]);
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        const validRemaining = allSubcontractorIds.filter((sid) => next.has(sid));
        if (validRemaining.length === 0) return new Set();
      } else {
        next.add(id);
        if (allSubcontractorIds.every((sid) => next.has(sid))) return new Set();
      }
      return next;
    });
  }

  // Sorted by descending machine count so the most-used category is first.
  const sortedCategories = useMemo(() => {
    const counts: Record<string, number> = {};
    machines.forEach((m) => {
      if (!m.category) return;
      counts[m.category] = (counts[m.category] ?? 0) + 1;
    });
    return Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a] || a.localeCompare(b)
    );
  }, [machines]);

  // Reset category filter when project changes (stale categories from previous project)
  const categoriesKey = sortedCategories.join(',');
  useEffect(() => {
    if (sortedCategories.length === 0) return;
    setCategoryFilter((prev) => {
      if (prev.size === 0) return prev;
      const validCategories = new Set(sortedCategories);
      const hasAnyValid = [...prev].some((c) => validCategories.has(c));
      return hasAnyValid ? prev : new Set();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesKey]);

  function toggleCategory(category: string) {
    setCategoryFilter((prev) => {
      if (prev.size === 0) {
        return new Set([category]);
      }
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
        const validRemaining = sortedCategories.filter((c) => next.has(c));
        if (validRemaining.length === 0) return new Set();
      } else {
        next.add(category);
        if (sortedCategories.every((c) => next.has(c))) return new Set();
      }
      return next;
    });
  }

  // Sorted by descending machine count. Different spellings/accents of the same free-text
  // value (e.g. "café" vs "cafe", "Rijplaten" vs "rijplaten") are grouped into a single entry;
  // the most frequently used raw spelling is shown as the label.
  const sortedOptions = useMemo(() => {
    const groups: Record<string, { count: number; labelCounts: Record<string, number> }> = {};
    machines.forEach((m) => {
      m.options?.forEach((raw) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        const key = normalizeForCompare(trimmed);
        const group = groups[key] ?? (groups[key] = { count: 0, labelCounts: {} });
        group.count += 1;
        group.labelCounts[trimmed] = (group.labelCounts[trimmed] ?? 0) + 1;
      });
    });
    return Object.entries(groups)
      .map(([key, g]) => {
        const label = Object.entries(g.labelCounts).sort(
          (a, b) => b[1] - a[1] || a[0].localeCompare(b[0])
        )[0][0];
        return { key, label, count: g.count };
      })
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .map(({ key, label }) => ({ key, label }));
  }, [machines]);

  // Reset options filter when project changes (stale keys from previous project)
  const optionsKey = sortedOptions.map((o) => o.key).join(',');
  useEffect(() => {
    if (sortedOptions.length === 0) return;
    setOptionsFilter((prev) => {
      if (prev.size === 0) return prev;
      const validKeys = new Set(sortedOptions.map((o) => o.key));
      const hasAnyValid = [...prev].some((k) => validKeys.has(k));
      return hasAnyValid ? prev : new Set();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsKey]);

  function toggleOption(key: string) {
    setOptionsFilter((prev) => {
      const allKeys = sortedOptions.map((o) => o.key);
      if (prev.size === 0) {
        return new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        const validRemaining = allKeys.filter((k) => next.has(k));
        if (validRemaining.length === 0) return new Set();
      } else {
        next.add(key);
        if (allKeys.every((k) => next.has(k))) return new Set();
      }
      return next;
    });
  }

  const isAllAvailability = availabilityKeys.every((k) => availabilityFilter.has(k));
  const isAllStatus = ALL_STATUS.every((k) => statusFilter.has(k));
  const isAllEngineType = ALL_ENGINE_TYPE.every((k) => engineTypeFilter.has(k));
  const isAllSites =
    siteFilter.size === 0 || sites.length === 0 || sites.every((s) => siteFilter.has(s.id));
  const isAllSuppliers =
    supplierFilter.size === 0 || uniqueSuppliers.length === 0 || uniqueSuppliers.every((s) => supplierFilter.has(s.id));
  // Unlike sites/suppliers, sortedSubcontractors is itself date-window-scoped (only currently
  // active bookings), so it can legitimately be empty while a stale selection still restricts
  // filteredMachines — normalizing against it here would vacuously treat that as "all".
  const isAllSubcontractors = subcontractorFilter.size === 0;
  const isAllCategories =
    categoryFilter.size === 0 || sortedCategories.length === 0 || sortedCategories.every((c) => categoryFilter.has(c));
  const isAllOptions =
    optionsFilter.size === 0 || sortedOptions.length === 0 || sortedOptions.every((o) => optionsFilter.has(o.key));
  const isAllPickup = ALL_PICKUP.every((k) => pickupFilter.has(k));

  // Fuse index is rebuilt only when machines change (project switch or mutation).
  // machine_number is intentionally excluded — sequential codes like SN-001/SN-002 differ by
  // only one character and would produce false fuzzy matches. Those are matched via exact
  // includes() below.
  const fuse = useMemo(
    () =>
      new Fuse(machines, {
        keys: [
          { name: 'name', weight: 2 },
          'category',
          'options',
          'notes',
          'supplier.name',
          'address.name',
          'address.street',
          'address.city',
          'bookings.sites.name',
          'bookings.subcontractor.name',
          'bookings.subcontractor_user.first_name',
          'bookings.subcontractor_user.last_name',
        ],
        threshold: 0.35,
        minMatchCharLength: 1,
        ignoreLocation: true,
      }),
    [machines]
  );

  const filteredMachines = useMemo(() => {
    if (dashboardFilter) {
      if (dashboardFilter === 'rental_extension' || dashboardFilter === 'overdue_return') {
        const ids = new Set(dashboardFilterMachineIds ?? []);
        return machines.filter((m) => ids.has(m.id));
      }
      const today = appNow().format('YYYY-MM-DD');
      const tomorrow = appNow().add(1, 'day').format('YYYY-MM-DD');
      return machines.filter((m) => {
        if (dashboardFilter === 'arriving') {
          return m.start_date === today || m.start_date === tomorrow;
        }
        if (dashboardFilter === 'pickup_confirmed') {
          return m.pickup_on_end_date === true && (m.end_date === today || m.end_date === tomorrow);
        }
        if (dashboardFilter === 'to_order_action') {
          return m.status === 'to_order' && (m.start_date === today || m.start_date === tomorrow);
        }
        // pickup_action
        return m.pickup_on_end_date !== true && (m.end_date === today || m.end_date === tomorrow);
      });
    }

    const allAvail = availabilityKeys.every((k) => availabilityFilter.has(k));
    const allStat = ALL_STATUS.every((k) => statusFilter.has(k));
    const allEngineType = ALL_ENGINE_TYPE.every((k) => engineTypeFilter.has(k));
    const allSit =
      siteFilter.size === 0 || sites.length === 0 || sites.every((s) => siteFilter.has(s.id));
    const allSuppl =
      supplierFilter.size === 0 || uniqueSuppliers.length === 0 || uniqueSuppliers.every((s) => supplierFilter.has(s.id));
    const allSub = subcontractorFilter.size === 0;
    const allCat =
      categoryFilter.size === 0 || sortedCategories.length === 0 || sortedCategories.every((c) => categoryFilter.has(c));
    const allOpt =
      optionsFilter.size === 0 || sortedOptions.length === 0 || sortedOptions.every((o) => optionsFilter.has(o.key));
    const allPick = ALL_PICKUP.every((k) => pickupFilter.has(k));

    const q = search.trim();
    const fuseMatchIds: Set<string> | null = q
      ? new Set(fuse.search(q).map((r) => r.item.id))
      : null;

    const today = appNow().format('YYYY-MM-DD');

    return machines.filter((m) => {
      if (!allAvail && !availabilityFilter.has(getMachineAvailability(m, pickupHandinEnabled))) return false;
      if (!allStat && !statusFilter.has(m.status)) return false;
      if (!allEngineType && !engineTypeFilter.has(m.engine_type)) return false;
      if (!allSit && !m.bookings?.some((bk) => siteFilter.has(bk.site_id) && bk.start_date <= today && bk.end_date >= today)) return false;
      if (!allSuppl && !supplierFilter.has(m.supplier_id ?? '')) return false;
      if (!allSub && !m.bookings?.some((bk) => subcontractorFilter.has(bk.subcontractor_id) && bk.start_date <= today && bk.end_date >= today)) return false;
      if (!allCat && !categoryFilter.has(m.category ?? '')) return false;
      if (!allOpt && !m.options?.some((o) => optionsFilter.has(normalizeForCompare(o)))) return false;
      if (!allPick) {
        const isConfirmed = m.pickup_on_end_date === true;
        if (pickupFilter.has('confirmed') && !pickupFilter.has('not_confirmed') && !isConfirmed) return false;
        if (pickupFilter.has('not_confirmed') && !pickupFilter.has('confirmed') && isConfirmed) return false;
      }
      if (fuseMatchIds !== null) {
        const serialMatch = m.machine_number?.toLowerCase().includes(q.toLowerCase()) ?? false;
        if (!fuseMatchIds.has(m.id) && !serialMatch) return false;
      }
      return true;
    });
  }, [machines, availabilityFilter, statusFilter, engineTypeFilter, siteFilter, supplierFilter, subcontractorFilter, categoryFilter, optionsFilter, pickupFilter, sites, uniqueSuppliers, sortedCategories, sortedOptions, search, fuse, dashboardFilter, dashboardFilterMachineIds, pickupHandinEnabled, availabilityKeys]);

  const activeFilterCount = dashboardFilter
    ? 1
    : (isAllAvailability ? 0 : availabilityFilter.size) +
      (isAllStatus ? 0 : statusFilter.size) +
      (isAllEngineType ? 0 : engineTypeFilter.size) +
      (isAllSites ? 0 : siteFilter.size) +
      (isAllSuppliers ? 0 : supplierFilter.size) +
      (isAllSubcontractors ? 0 : subcontractorFilter.size) +
      (isAllCategories ? 0 : categoryFilter.size) +
      (isAllOptions ? 0 : optionsFilter.size) +
      (isAllPickup ? 0 : pickupFilter.size) +
      (search !== '' ? 1 : 0);

  // Only include sites that appear in at least one currently active machine booking, sorted by
  // booking count. A site used by only one machine (even if by multiple bookings) still counts —
  // the filter is only useful when ≥2 distinct sites are present (FilterPanel checks sites.length > 1).
  const sortedSites = useMemo(() => {
    const today = appNow().format('YYYY-MM-DD');
    const counts: Record<string, number> = {};
    machines.forEach((m) => {
      m.bookings?.forEach((bk) => {
        if (bk.start_date > today || bk.end_date < today) return;
        counts[bk.site_id] = (counts[bk.site_id] ?? 0) + 1;
      });
    });
    return [...sites]
      .filter((s) => (counts[s.id] ?? 0) > 0)
      .sort((a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0));
  }, [machines, sites]);

  return {
    availabilityFilter,
    toggleAvailability,
    setAvailabilityFilter,
    statusFilter,
    toggleStatus,
    setStatusFilter,
    engineTypeFilter,
    toggleEngineType,
    setEngineTypeFilter,
    siteFilter,
    toggleSite,
    setSiteFilter,
    supplierFilter,
    toggleSupplier,
    setSupplierFilter,
    uniqueSuppliers,
    subcontractorFilter,
    toggleSubcontractor,
    setSubcontractorFilter,
    sortedSubcontractors,
    categoryFilter,
    toggleCategory,
    setCategoryFilter,
    sortedCategories,
    optionsFilter,
    toggleOption,
    setOptionsFilter,
    sortedOptions,
    pickupFilter,
    togglePickup,
    setPickupFilter,
    dashboardFilter,
    clearDashboardFilter,
    sortedSites,
    search,
    setSearch,
    filterOpen,
    setFilterOpen,
    filteredMachines,
    activeFilterCount,
  };
}
